import { useQuery } from "@tanstack/react-query";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { buildQueryParams, type WasteEvent } from "@/hooks/use-waste-events";
import type { WasteFilters } from "@/hooks/use-waste-filters";
import { useTranslation } from "@/i18n/use-translation";
import { apiError } from "@/lib/api-error";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

type Range = { from: string | null; to: string | null };

/**
 * The Proof (POS2-868) reads the same list endpoint as Transactions, so it
 * honours the dashboard's active filters, plus fixed overrides: successful,
 * image-backed, identified (a named waste line, not L1), highest cost first.
 * `limit` sets how deep the client dedupe-by-line window reaches — 24 leaves
 * ample headroom to fill 4 lines.
 */
export function proofParams(filters: WasteFilters, range: Range, limit: number): URLSearchParams {
  const params = buildQueryParams(
    {
      ...filters,
      status: "successful",
      sort_by: "cost_estimate",
      sort_dir: "desc",
      page: 0,
      page_size: limit,
    },
    range,
  );
  params.set("has_image", "true");
  // Unidentified (L1) waste stays in the totals but is never listed — keep its
  // capture cards and count (X-Total-Count) out of The Proof and the Kitchen
  // display's limit=1 tile, which client-side filtering can't fix (POS2-924).
  params.set("identified", "true");
  return params;
}

export type ProofEventsResult = { events: WasteEvent[]; total: number };

/** Image-backed captures for the period, richest first, with the period's total
 * image-backed count (X-Total-Count) for the header. */
export function useProofEvents(
  filters: WasteFilters,
  range: Range,
  limit = 24,
  options?: { refetchInterval?: number },
) {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const params = proofParams(filters, range, limit);

  return useQuery<ProofEventsResult>({
    queryKey: ["proof-events", params.toString()],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/waste/events?${params.toString()}`, { headers });
      if (!res.ok) {
        throw await apiError(t, res);
      }
      const body = (await res.json()) as { data: WasteEvent[] };
      const total = Number.parseInt(res.headers.get("X-Total-Count") ?? "0", 10) || 0;
      return { events: body.data, total };
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
    refetchInterval: options?.refetchInterval,
    placeholderData: (prev) => prev,
  });
}

/** Just the image-backed capture count for a period — the "{n} last period"
 * figure in the empty state. limit=1 keeps the payload tiny; the true count
 * rides on X-Total-Count. Skipped for an unbounded (all-time) range. */
export function useProofCount(filters: WasteFilters, range: Range) {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const params = proofParams(filters, range, 1);

  return useQuery<number>({
    queryKey: ["proof-count", params.toString()],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/waste/events?${params.toString()}`, { headers });
      if (!res.ok) throw await apiError(t, res);
      return Number.parseInt(res.headers.get("X-Total-Count") ?? "0", 10) || 0;
    },
    enabled: !!headers.Authorization.replace("Bearer ", "") && !!range.from,
    placeholderData: (prev) => prev,
  });
}
